import _assign from "object-assign";

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { forwardRef, useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { base, icon, iconNames as names, deprecatedIconNames as deprecatedNames } from '@jpmuitk/style';
import { withStyles } from '@jpmuitk/theme';
import IconClassNameContext from './internal/IconClassNameContext';
import { style } from './style';
var sizes = ['small', 'medium', 'large'];
/*
  this component relies on the foundation (<brand>.css) file from
  the style package to get the fonticon set.
  however, all applications should be including this
  by default, something to check if nothing is being displayed
  The list of available icons is available on the toolkit site
*/
// get the string passed as either the new accessibleText prop or
// as human readable name of the icon

var getAccessibleString = function getAccessibleString(accessibleText, name) {
  if (typeof accessibleText === 'boolean') {
    if (accessibleText) {
      return name;
    }
  } else {
    return accessibleText;
  }
}; // return the accessible text element with hiddenStyle props


var getAccessibleText = function getAccessibleText(accessibleText, brand, name) {
  if (accessibleText) {
    return /*#__PURE__*/React.createElement("span", {
      style: base.accessibleText
    }, getAccessibleString(accessibleText, name));
  }
};

process.env.NODE_ENV !== "production" ? getAccessibleText.propTypes = {
  accessibleText: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  brand: PropTypes.string,
  name: PropTypes.string
} : void 0; // returns the style tag for character code

var getIconCharStyle = function getIconCharStyle(brand, name, iconStyleName) {
  if (icon[iconStyleName]) {
    return /*#__PURE__*/React.createElement("style", null, ".".concat(brand, "-icon-").concat(name, ":before { content: ").concat(icon[iconStyleName]['&:before'].content, "}"));
  }
};

process.env.NODE_ENV !== "production" ? getIconCharStyle.propTypes = {
  brand: PropTypes.string,
  name: PropTypes.string
} : void 0;

function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

var Icon = /*#__PURE__*/forwardRef(function Icon(_ref, ref) {
  var accessibleText = _ref.accessibleText,
      _ref$brand = _ref.brand,
      brand = _ref$brand === void 0 ? 'jpmuitk' : _ref$brand,
      className = _ref.className,
      classes = _ref.classes,
      name = _ref.name,
      onClick = _ref.onClick,
      _ref$size = _ref.size,
      sizeProp = _ref$size === void 0 ? 'small' : _ref$size,
      rest = _objectWithoutProperties(_ref, ["accessibleText", "brand", "className", "classes", "name", "onClick", "size"]);

  var classNameFromContext = useContext(IconClassNameContext);
  var isSize = sizes.indexOf(sizeProp) !== -1; // this looks strange but seemed to be the easiest way to achieve the firstUpper

  var iconStyleName = "Icon".concat(name.split('-').map(function (part) {
    return capitalize(part);
  }).join(''));
  return /*#__PURE__*/React.createElement("span", _assign({
    className: classnames("".concat(brand, "-wrap-icon"), classes.root, className, isSize && classes[sizeProp], classNameFromContext),
    onClick: onClick
  }, rest, {
    ref: ref
  }), getIconCharStyle(brand, name, iconStyleName), /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true",
    className: classnames("".concat(brand, "-icon-").concat(name), classes.content),
    style: !isSize ? {
      fontSize: "".concat(sizeProp, "px")
    } : undefined
  }), getAccessibleText(accessibleText, brand, name));
});
process.env.NODE_ENV !== "production" ? Icon.propTypes = {
  /**
   * Used to include HAT (hidden accessible text)
   * Defaults to icon name if used without value
   * will be set to custom HAT text if passed a string
   */
  accessibleText: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),

  /**
   * Prefix for icon classes
   */
  brand: PropTypes.string,

  /**
   * The className(s) of the component
   */
  className: PropTypes.string,

  /**
   * material-ui
   * Useful to extend the style applied to components.
   */
  classes: PropTypes.objectOf(PropTypes.string),

  /**
   * Name of the icon.
   * See go/uitoolkit -> Documentation -> Iconography
   */
  name: PropTypes.string.isRequired,

  /**
   * On click event handler
   */
  onClick: PropTypes.func,

  /**
   * Size of the icon, explicit size or small/medium/large
   */
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(sizes)])
} : void 0; // TODO (BREAKING CHANGE REQUIRED)
// https://issuetracking.jpmchase.net/jira12/browse/JPMUITK-2835
// Add unique JPMUI prefix to name to identify component as a Toolkit component

export default withStyles(style, {
  name: 'Icon'
})(Icon);
export { names, deprecatedNames };